import { IcEye, IcEyeKo, IcLock } from '@assets/icons/24x24';
import {
  ButtonProps,
  GroupProps,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputProps,
  mergeRefs,
  useControllableState,
} from '@chakra-ui/react';
import { forwardRef, ReactNode, useRef } from 'react';
import { useIntl } from 'react-intl';

const VisibilityTrigger = forwardRef<HTMLButtonElement, ButtonProps>(function VisibilityTrigger(props, ref) {
  return (
    <IconButton
      ref={ref}
      tabIndex={-1}
      variant="transparent"
      aspectRatio="square"
      size="sm"
      aria-label="password visibility"
      paddingInline={0}
      {...props}
    />
  );
});

export interface PasswordVisibilityProps {
  defaultVisible?: boolean;
  visible?: boolean;
  onVisibleChange?: (visible: boolean) => void;
  visibilityIcon?: { on: ReactNode; off: ReactNode };
}

export interface PasswordInputProps extends InputProps, PasswordVisibilityProps {
  rootProps?: GroupProps;
  showLockIcon?: boolean;
}
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(function PasswordInput(props, ref) {
  const {
    rootProps,
    defaultVisible,
    visible: visibleProp,
    onVisibleChange,
    visibilityIcon = { on: <Icon as={IcEyeKo} boxSize="24px" />, off: <Icon as={IcEye} boxSize="24px" /> },
    showLockIcon = false,
    ...rest
  } = props;
  const { formatMessage } = useIntl();

  const [visible, setVisible] = useControllableState({
    value: visibleProp,
    defaultValue: defaultVisible || false,
    onChange: onVisibleChange,
  });

  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <InputGroup
      {...(showLockIcon && {
        startElement: <Icon as={IcLock} color="neutral.500" />,
        startElementProps: { paddingInline: 2 },
      })}
      endElement={
        <VisibilityTrigger
          disabled={rest.disabled}
          onPointerDown={(e) => {
            if (rest.disabled) return;
            if (e.button !== 0) return;
            e.preventDefault();
            setVisible(!visible);
          }}
        >
          {visible ? visibilityIcon.off : visibilityIcon.on}
        </VisibilityTrigger>
      }
      endElementProps={{ paddingInline: 2 }}
      {...rootProps}
    >
      <Input
        {...rest}
        ref={mergeRefs(ref, inputRef)}
        type={visible ? 'text' : 'password'}
        placeholder={formatMessage({ id: 'authent.user.password' })}
        style={{
          paddingInlineStart: showLockIcon ? '52px' : '16px',
          paddingInlineEnd: '52px',
        }}
      />
    </InputGroup>
  );
});
